import { useState } from 'react';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { DEFAULT_TIMEZONE } from '../../../../constants/appointments';
import useUser from '../../../../hooks/global/useUser';
import { LoadedUser, Transaction } from '../../../../interfaces';
import { convertPaymentsToTransactions } from '../../../../helpers/payments';
import { PaymentWithTransfers } from '../../../../types/payments';
import useGetCustomerPaymentsTab from '../../../../hooks/customers/useGetCustomerPaymentsTab';
import TransactionDetailModal from '../../../transactions/TransactionDetailModal';
import PaymentsStatsRow from './PaymentsStatsRow';
import PaymentsTable from './PaymentsTable';
import ShowAllLink from './ShowAllLink';

interface Props {
  customerId: string;
  businessId: string;
}

function PaymentsTab({ customerId, businessId }: Props) {
  const { user } = useUser<LoadedUser>();
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);
  const {
    transactions,
    totals,
    isInitialLoading,
    isFetching,
    hasMore,
    loadMore,
    isError,
    isFetchNextPageError,
  } = useGetCustomerPaymentsTab({ customerId, businessId });

  const onSelectPayment = (payment: PaymentWithTransfers) => {
    const [transaction] = convertPaymentsToTransactions([payment]);
    setSelectedTransaction(transaction);
  };

  if (isInitialLoading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: '200px' }}>
        <CircularProgress color="primary" />
      </Stack>
    );
  }

  if (isError || !totals) {
    return (
      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
        There was an error loading this customer&apos;s payments.
      </Typography>
    );
  }

  if (transactions.length === 0) {
    return (
      <>
        <PaymentsStatsRow totals={totals} />
        <Typography
          variant="body1"
          sx={{ color: 'text.secondary', marginTop: '28px' }}>
          No transactions yet.
        </Typography>
      </>
    );
  }

  return (
    <>
      <PaymentsStatsRow totals={totals} />
      <PaymentsTable
        transactions={transactions}
        timezone={user?.timezone ?? DEFAULT_TIMEZONE}
        onSelectPayment={onSelectPayment}
      />
      {isFetchNextPageError && (
        <Typography
          variant="body1"
          sx={{ color: 'text.secondary', marginTop: '28px' }}>
          There was an error loading more payments.
        </Typography>
      )}
      {hasMore && (
        <ShowAllLink
          label={isFetchNextPageError ? 'Retry' : 'Show more'}
          isLoading={isFetching}
          onClick={loadMore}
        />
      )}
      {selectedTransaction && (
        <TransactionDetailModal
          open={!!selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
          transaction={selectedTransaction}
        />
      )}
    </>
  );
}

export default PaymentsTab;
